import { ReactNode } from "react";
import { DialogActions, DialogContent, DialogContentText, DialogTitle, Stack } from "@mui/material";
import { useTranslation } from "next-i18next";
import { Button, ButtonProps } from "src/components-mui/base/button/button";
import { Typography } from "src/components-mui/base/typography/typography";
import { WfDialog } from "src/components-mui/common/dialog/wf-dialog";

interface IWfConfirmDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    header: string;
    body: ReactNode;
    confirmButtonText: string;
    cancelButtonText?: string;
    confirmButtonProps?: Omit<ButtonProps, "onClick">;
    wikifolioShortDescription?: string;
    container?: () => HTMLElement | null;
    showCloseButton?: boolean;
}

const DIALOG_TITLE_ID = "wf-confirm-dialog-title";
const DIALOG_DESCRIPTION_ID = "wf-confirm-dialog-description";

export const WfConfirmDialog = ({
    isOpen,
    onClose,
    onConfirm,
    header,
    body,
    confirmButtonText,
    cancelButtonText,
    confirmButtonProps,
    wikifolioShortDescription,
    container,
    showCloseButton,
}: IWfConfirmDialogProps) => {
    const { t } = useTranslation("common");

    return (
        <WfDialog
            isOpen={isOpen}
            onClose={onClose}
            ariaLabelledBy={DIALOG_TITLE_ID}
            ariaDescribedBy={DIALOG_DESCRIPTION_ID}
            container={container}
            showCloseButton={showCloseButton}
            sx={{ boxShadow: 5 }}
        >
            <DialogTitle sx={{ paddingLeft: 0, paddingTop: [6, 0], paddingBottom: 3 }} id={DIALOG_TITLE_ID}>
                <Typography
                    component="span"
                    sx={{
                        fontSize: ["xl", "2xl"],
                        fontWeight: "bold",
                    }}
                >
                    {header}
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ paddingX: 0, paddingBottom: 5 }}>
                <DialogContentText component="div" sx={{ paddingLeft: 0, paddingTop: 0, color: "gray.800" }} id={DIALOG_DESCRIPTION_ID}>
                    {body}
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 0 }}>
                <Stack
                    direction={{ xs: "column", sm: "row-reverse" }}
                    spacing={2}
                    sx={{
                        width: { xs: "100%", sm: "auto" },
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={onConfirm}
                        {...confirmButtonProps}
                        data-gtm-wikifolio-short-description={wikifolioShortDescription}
                    >
                        {confirmButtonText}
                    </Button>
                    <Button variant="outlined" onClick={onClose}>
                        {cancelButtonText ?? t("cancel")}
                    </Button>
                </Stack>
            </DialogActions>
        </WfDialog>
    );
};
